import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { roundsAPI } from '../../services/api';

/**
 * Guest scoring entry point — finds the live 'open' round(s), lets a guest
 * pick their group, then hands off to the shared ActiveRound enter-scores tab.
 * Mirrors FriDaveScoring's pattern.
 */
export default function ClubhouseScoring() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [round, setRound] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveRounds();
  }, []);

  const fetchActiveRounds = async () => {
    try {
      setLoading(true);
      const all = await roundsAPI.getAll({ eventType: 'open' }).catch(() => []);
      const live = (Array.isArray(all) ? all : []).filter(
        r => r.status === 'in_progress' || r.status === 'active'
      );
      setRounds(live);

      // If a group is already saved for a live round, go straight to score entry
      for (const r of live) {
        const savedGroup = localStorage.getItem(`round_${r.id}_group`);
        if (savedGroup) {
          navigate(`/clubhouse/rounds/${r.id}`, { state: { activeTab: 'enter-scores' } });
          return;
        }
      }

      // Exactly one live round: load its groups directly
      if (live.length === 1) {
        const fullRound = await roundsAPI.getById(live[0].id);
        setRound(fullRound);
        const grouped = {};
        (fullRound.players || []).forEach(p => {
          const g = p.group_number || 1;
          if (!grouped[g]) grouped[g] = [];
          grouped[g].push(p);
        });
        setGroups(Object.entries(grouped).sort((a, b) => Number(a[0]) - Number(b[0])));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (roundId, groupNumber) => {
    localStorage.setItem(`round_${roundId}_group`, groupNumber.toString());
    navigate(`/clubhouse/rounds/${roundId}`, { state: { activeTab: 'enter-scores' } });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  if (rounds.length === 0) {
    return (
      <div className="px-4 pb-4">
        <div className="bg-ch-surface rounded-2xl p-10 text-center shadow-sm border border-ch-secondary/15">
          <h2 className="text-xl font-bold text-ch-primary mb-2">No Round in Progress</h2>
          <p className="text-ch-secondary/70 text-sm">Score entry is only available while a round is live.</p>
        </div>
      </div>
    );
  }

  // More than one guest round live at once (e.g. two groups' events overlap):
  // let them pick which event/round first, then their group.
  if (rounds.length > 1 && !round) {
    return (
      <div className="px-4 pb-4">
        <p className="text-sm font-semibold text-ch-secondary/70 mb-3 uppercase tracking-wide">
          Select Your Round
        </p>
        <div className="space-y-3">
          {rounds.map(r => (
            <button
              key={r.id}
              onClick={async () => {
                setLoading(true);
                const fullRound = await roundsAPI.getById(r.id);
                setRound(fullRound);
                const grouped = {};
                (fullRound.players || []).forEach(p => {
                  const g = p.group_number || 1;
                  if (!grouped[g]) grouped[g] = [];
                  grouped[g].push(p);
                });
                setGroups(Object.entries(grouped).sort((a, b) => Number(a[0]) - Number(b[0])));
                setLoading(false);
              }}
              className="w-full bg-ch-surface rounded-2xl shadow-sm border border-ch-secondary/15 p-4 text-left hover:border-ch-accent/40 transition-all"
            >
              <p className="text-xs font-bold uppercase tracking-wide text-ch-accent mb-1">
                {r.event_name} — Round {r.round_number}
              </p>
              <p className="text-sm text-ch-secondary">{r.course_name}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      <div className="bg-ch-surface rounded-2xl p-4 mb-4 border border-ch-secondary/15 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-ch-secondary/50 mb-0.5">Round in Progress</p>
            <h2 className="text-lg font-bold text-ch-primary">{round.event_name}</h2>
          </div>
          <span className="px-2 py-1 bg-ch-accent/15 text-ch-accent text-xs font-bold rounded-full animate-pulse">LIVE</span>
        </div>
      </div>

      <p className="text-sm font-semibold text-ch-secondary/70 mb-3 uppercase tracking-wide">Select Your Group</p>
      <div className="space-y-3">
        {groups.map(([groupNum, players]) => (
          <button
            key={groupNum}
            onClick={() => handleGroupSelect(round.id, Number(groupNum))}
            className="w-full bg-ch-surface rounded-2xl shadow-sm border border-ch-secondary/15 p-4 text-left hover:border-ch-accent/40 hover:shadow-md transition-all"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-ch-accent mb-2">Group {groupNum}</p>
            <div className="flex flex-wrap gap-2">
              {players.map(p => (
                <span key={p.player_id} className="text-sm font-semibold text-ch-secondary bg-ch-bg px-3 py-1 rounded-full border border-ch-secondary/15">
                  {p.player_name}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
